import { GOOGLE_MAPS_CONFIG } from "@app/configs/map";
import {
  geocodeSearch,
  getRouteFromCoordsToAddress,
} from "@shared/api/voiceBot/endpoints";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

interface MapManagerProps {
  onApiStart: () => void;
  onApiComplete: () => void;
}

export const useMapManager = ({
  onApiStart,
  onApiComplete,
}: MapManagerProps) => {
  const [mapSearchQuery, setMapSearchQuery] = useState<string>("");
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [isMapVisible, setIsMapVisible] = useState<boolean>(false);
  const [currentNavigationDestination, setCurrentNavigationDestination] =
    useState<string | null>(null);
  const [autocompleteSearch, setAutocompleteSearch] =
    useState<string | null>(null);
  const [pendingNavigationAddress, setPendingNavigationAddress] = useState<
    string | null
  >(null);
  const [currentLocation, setCurrentLocation] = useState<string>("");

  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  // ---------- POLYLINE DECODER ----------
  const decodePolyline = useCallback(async (encoded: string) => {
    const points: [number, number][] = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < encoded.length) {
      let b,
        shift = 0,
        result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += dlat;

      shift = 0;
      result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);

      const dlng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += dlng;

      points.push([lng / 1e5, lat / 1e5]);
    }

    return {
      type: "LineString",
      coordinates: points,
    };
  }, []);

  // ---------- MAP MARKERS ----------
  // For Google Maps, markers are handled internally by GoogleMap component
  const addCurrentLocationMarker = useCallback(
    (lat: number, lng: number) => {
      if (!mapRef.current) return;

      if (window.google && window.google.maps) {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: mapRef.current,
          title: "Vị trí hiện tại",
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#1890ff",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
        });
        markersRef.current.push(marker);
      }
    },
    []
  );

  const zoomToCurrentLocation = useCallback(
    (lat: number, lng: number) => {
      if (!mapRef.current) {
        console.log("Map not ready for zooming");
        return;
      }

      if (window.google && window.google.maps) {
        mapRef.current.setCenter({ lat, lng });
        mapRef.current.setZoom(15);
      }
    },
    []
  );

  const addDestinationMarker = useCallback(
    (lat: number, lng: number, title: string) => {
      if (!mapRef.current) return;

      if (window.google && window.google.maps) {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: mapRef.current,
          title: title,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: "#ff6b6b",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
        });
        markersRef.current.push(marker);
      }
    },
    []
  );

  // ---------- CLEAR MAP MARKERS ----------
  const clearAllMarkers = useCallback(() => {
    if (!mapRef.current) return;

    markersRef.current.forEach((marker) => {
      if (marker && marker.setMap) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];
  }, []);

  // ---------- ROUTE HANDLING ----------
  // Google Maps route rendering is handled by GoogleMap component via Directions API
  const addRouteToMap = useCallback(
    (routeGeometry: any) => {
      // Route rendering is handled by GoogleMap component
      // This function is kept for compatibility but does nothing
      // as Google Maps handles routes via DirectionsService
    },
    []
  );

  const fitMapToRoute = useCallback(
    (routeGeometry: any) => {
      if (!mapRef.current) return;

      if (window.google && window.google.maps) {
        const coordinates = routeGeometry.coordinates;
        const bounds = new window.google.maps.LatLngBounds();
        coordinates.forEach((coord: [number, number]) => {
          bounds.extend({ lat: coord[1], lng: coord[0] });
        });
        mapRef.current.fitBounds(bounds);
      }
    },
    []
  );

  // ---------- NAVIGATION TO SPECIFIC ADDRESS ----------
  const navigateToAddress = useCallback(
    async (destinationAddress: string, retryCount: number = 0) => {
      if (!mapRef.current) {
        if (retryCount < 10) {
          setTimeout(() => {
            navigateToAddress(destinationAddress, retryCount + 1);
          }, 500);
          return;
        } else {
          console.error("Map not available after 10 retries");
          return;
        }
      }

      try {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const currentLat = position.coords.latitude;
            const currentLng = position.coords.longitude;
            const origin = `${currentLat}, ${currentLng}`;

            onApiStart();

            try {
              const response = await getRouteFromCoordsToAddress({
                origin,
                destination_address: destinationAddress,
                vehicle: "car",
                points_encoded: true,
              });

              const data: any = response;

              if (data && data.paths && data.paths.length > 0) {
                const route = data.paths[0];
                const routeGeometry = await decodePolyline(route.points);

                // Clear all existing markers and routes before adding new ones
                clearAllMarkers();

                addRouteToMap(routeGeometry);
                addDestinationMarker(
                  data.destination_info.latitude,
                  data.destination_info.longitude,
                  destinationAddress
                );
                addCurrentLocationMarker(currentLat, currentLng);
                fitMapToRoute(routeGeometry);
              } else {
                console.log("❌ No route found");
              }
            } catch (apiError) {
              console.error("Error creating route:", apiError);
            } finally {
              onApiComplete();
            }
          },
          async (error) => {
            const origin = `${GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LAT}, ${GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LNG}`;

            onApiStart();

            try {
              const response = await getRouteFromCoordsToAddress({
                origin,
                destination_address: destinationAddress,
                vehicle: "car",
                points_encoded: true,
              });

              const data: any = response;

              if (data && data.paths && data.paths.length > 0) {
                const route = data.paths[0];
                const routeGeometry = await decodePolyline(route.points);

                // Clear all existing markers and routes before adding new ones
                clearAllMarkers();

                addRouteToMap(routeGeometry);
                addDestinationMarker(
                  data.destination_info.latitude,
                  data.destination_info.longitude,
                  destinationAddress
                );
                addCurrentLocationMarker(
                  GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LAT,
                  GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LNG
                );
                fitMapToRoute(routeGeometry);
              } else {
                console.log("❌ No route found");
              }
            } catch (apiError) {
              console.error("Error creating route:", apiError);
            } finally {
              onApiComplete();
            }
          }
        );
      } catch (error) {
        console.error("Error in navigateToAddress:", error);
      }
    },
    [
      decodePolyline,
      addRouteToMap,
      addCurrentLocationMarker,
      addDestinationMarker,
      fitMapToRoute,
      clearAllMarkers,
      onApiStart,
      onApiComplete,
    ]
  );

  // ---------- GET CURRENT LOCATION ----------
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.error("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Set current location string (formatted for display)
        setCurrentLocation(`${lat}, ${lng}`);

        // Add marker if map is available
        if (mapRef.current) {
          addCurrentLocationMarker(lat, lng);
          zoomToCurrentLocation(lat, lng);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setCurrentLocation(
          `${GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LAT}, ${GOOGLE_MAPS_CONFIG.DEFAULT_COORDINATES.LNG}`
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  }, [addCurrentLocationMarker, zoomToCurrentLocation]);

  // ---------- MAP HANDLERS ----------
  const handleMapLoad = useCallback((map: any) => {
    mapRef.current = map;
  }, []);

  const handleLocationSelect = useCallback((location: any) => {
    setSelectedLocation(location);
  }, []);

  const handleCurrentLocationFound = useCallback(
    (location: any) => {
      if (location.error) {
        console.error("Geolocation error:", location);
      } else {
        // Set current location string
        setCurrentLocation(`${location.lat}, ${location.lng}`);

        // Add marker first
        addCurrentLocationMarker(location.lat, location.lng);

        // Then zoom to location with a small delay to ensure marker is added
        setTimeout(() => {
          zoomToCurrentLocation(location.lat, location.lng);
        }, 200);
      }
    },
    [addCurrentLocationMarker, zoomToCurrentLocation]
  );

  const handleMapSearch = useCallback((query: string) => {
    setMapSearchQuery(query);
    if (mapRef.current) {
      // The Map component will handle the search automatically
    }
  }, []);

  // ---------- MAP INTENT HANDLER ----------
  const handleMapIntent = useCallback((data: any) => {
    if (data?.search_query) {
      setMapSearchQuery(data.search_query);
      setIsMapVisible(true);
    } else if (data?.show_map) {
      setIsMapVisible(true);
    } else if (data?.hide_map) {
      setIsMapVisible(false);
    }
  }, []);

  // ---------- GEOCODE SEARCH ----------
  const handleGeocodeSearch = useMutation({
    mutationFn: geocodeSearch,
    onSuccess: (data: any) => {
      if (data?.address) {
        if (
          currentNavigationDestination &&
          currentNavigationDestination !== data.address
        ) {
          setPendingNavigationAddress(data.address);
        } else {
          setCurrentNavigationDestination(data.address);
          navigateToAddress(data.address);
        }
      } else {
      }
      onApiComplete();
    },
    onError: (error: any) => {
      console.error("Geocode search error:", error);
      onApiComplete();
    },
  });

  return {
    // State
    mapSearchQuery,
    selectedLocation,
    isMapVisible,
    currentNavigationDestination,
    autocompleteSearch,
    pendingNavigationAddress,
    currentLocation,

    // Actions
    handleMapIntent,
    handleMapLoad,
    handleLocationSelect,
    handleCurrentLocationFound,
    handleMapSearch,
    navigateToAddress,
    clearAllMarkers,
    getCurrentLocation,
    setIsMapVisible,
    setCurrentNavigationDestination,
    setAutocompleteSearch,
    setPendingNavigationAddress,
    setCurrentLocation,

    // Mutations
    handleGeocodeSearch,
  };
};
